import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/home';
import { aboutInfo, teamMembers } from '@/mocks/about';
import { useState } from 'react';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9phafpn2trhb35gvfc0';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormState('success');
      return;
    }
    formData.delete('phone_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormState('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: aboutInfo.brandName,
    url: siteInfo.domain,
    foundingDate: aboutInfo.founded,
    description: aboutInfo.introduction[0],
    address: {
      '@type': 'PostalAddress',
      addressLocality: aboutInfo.contact.address,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      telephone: aboutInfo.contact.phone,
      contactType: 'customer service',
      hoursAvailable: aboutInfo.contact.workingHours,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />

      <Breadcrumb
        items={[
          { name: '首页', path: '/' },
          { name: '关于我们' },
        ]}
      />

      {/* Brand Introduction */}
      <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-12">
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
            关于我们
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4">
            {aboutInfo.brandName}
          </h1>
          <p className="text-lg text-primary-600 font-medium">{aboutInfo.slogan}</p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="aspect-[21/9] rounded-2xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20international%20tech%20company%20headquarters%20with%20warm%20golden%20lighting%2C%20sleek%20architectural%20design%2C%20professional%20corporate%20atmosphere%2C%20glass%20building%20with%20green%20plants%2C%20clean%20minimalist%20style%2C%20sunset%20warm%20tones&width=1400&height=600&seq=about-hero&orientation=landscape"
              alt={aboutInfo.brandName}
              title={`${aboutInfo.brandName} - 关于我们`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="space-y-4">
            {aboutInfo.introduction.map((text, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {text}
              </p>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
            <div className="bg-background-100 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-primary-600">2019</div>
              <div className="text-xs text-foreground-500 mt-1">成立年份</div>
            </div>
            <div className="bg-background-100 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-primary-600">500万+</div>
              <div className="text-xs text-foreground-500 mt-1">全球用户</div>
            </div>
            <div className="bg-background-100 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-primary-600">80+</div>
              <div className="text-xs text-foreground-500 mt-1">覆盖国家</div>
            </div>
            <div className="bg-background-100 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-primary-600">7×24</div>
              <div className="text-xs text-foreground-500 mt-1">全天候服务</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="w-full bg-background-100 py-16 md:py-20">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
              品牌价值观
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              我们坚守的核心理念
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutInfo.values.map((v, i) => (
              <div key={i} className="bg-background-50 rounded-2xl p-6 text-center border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-xl">
                  <i className={`${v.icon} text-accent-600 text-xl`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                <p className="text-xs text-foreground-500 leading-relaxed">{v.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="w-full bg-background-50 py-16 md:py-20">
        <div className="w-full max-w-[900px] mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-4">
              发展历程
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              我们的成长之路
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-background-200"></div>
            <div className="space-y-6">
              {aboutInfo.milestones.map((m, i) => (
                <div key={i} className="relative pl-14 flex items-start gap-4">
                  <div className="absolute left-0 w-10 h-10 flex items-center justify-center bg-secondary-500 text-background-50 rounded-full text-xs font-bold">
                    {m.year}
                  </div>
                  <div className="bg-background-100 rounded-xl p-4 flex-1">
                    <p className="text-sm text-foreground-700">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="w-full bg-background-100 py-16 md:py-20">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
              核心团队
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              专业团队，值得信赖
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {teamMembers.map((member, i) => (
              <div key={i} className="bg-background-50 rounded-2xl p-6 text-center border border-background-200/70">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden">
                  <img
                    src={member.avatar}
                    alt={member.name}
                    title={`${member.name} - ${member.role}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">{member.name}</h3>
                <p className="text-xs text-primary-600 font-medium mb-2">{member.role}</p>
                <p className="text-xs text-foreground-500">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="w-full bg-background-50 py-16 md:py-20">
        <div className="w-full max-w-[900px] mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
              资质认证
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              国际权威认证
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {aboutInfo.certifications.map((cert, i) => (
              <div key={i} className="flex items-center gap-3 bg-background-100 rounded-xl p-4">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-accent-100 rounded-lg">
                  <i className="ri-verified-badge-line text-accent-600 text-lg"></i>
                </div>
                <span className="text-sm text-foreground-700 font-medium">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="w-full bg-primary-500 py-16 md:py-20">
        <div className="w-full max-w-[800px] mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
            联系我们
          </h2>
          <p className="text-background-50/80 text-sm mb-10 max-w-lg mx-auto">
            如果您有任何疑问或需要帮助，欢迎随时与我们的客服团队联系
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
            <div className="bg-background-50/10 backdrop-blur-sm rounded-xl p-5">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-background-50/20 rounded-lg">
                <i className="ri-mail-line text-background-50 text-lg"></i>
              </div>
              <p className="text-xs text-background-50/60 mb-1">电子邮箱</p>
              <p className="text-sm text-background-50 font-medium">{aboutInfo.contact.email}</p>
            </div>
            <div className="bg-background-50/10 backdrop-blur-sm rounded-xl p-5">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-background-50/20 rounded-lg">
                <i className="ri-phone-line text-background-50 text-lg"></i>
              </div>
              <p className="text-xs text-background-50/60 mb-1">客服电话</p>
              <p className="text-sm text-background-50 font-medium">{aboutInfo.contact.phone}</p>
            </div>
            <div className="bg-background-50/10 backdrop-blur-sm rounded-xl p-5">
              <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-background-50/20 rounded-lg">
                <i className="ri-time-line text-background-50 text-lg"></i>
              </div>
              <p className="text-xs text-background-50/60 mb-1">服务时间</p>
              <p className="text-sm text-background-50 font-medium">{aboutInfo.contact.workingHours}</p>
            </div>
          </div>

          {formState === 'success' ? (
            <div className="bg-background-50 rounded-2xl p-8 max-w-md mx-auto">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
                <i className="ri-check-line text-accent-600 text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">消息已发送</h3>
              <p className="text-sm text-foreground-500">感谢您的留言，我们的客服团队将尽快与您联系。</p>
              <button
                onClick={() => setFormState('idle')}
                className="mt-4 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                继续留言
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 rounded-2xl p-6 md:p-8 max-w-md mx-auto text-left"
            >
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5 text-center">在线留言</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1.5">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  disabled={formState === 'loading'}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {formState === 'loading' ? '发送中...' : '发送留言'}
                </button>
              </div>
              {formState === 'error' && formError && (
                <p className="text-sm text-red-500 mt-3 text-center">{formError}</p>
              )}
            </form>
          )}
        </div>
      </section>
    </div>
  );
}